// The repository's organization rules — folders, kinds, filtering, sorting and
// grouping — kept pure and out of `FileRepository.js`.
//
// Extracted for the `searchFiles` reason: that component cannot be rendered
// under jest (it is reached through the Dashboard/ProjectPage trees) and had no
// test at all, so every rule with a decision in it lives here where it can be
// pinned directly. It is also where the extension vocabulary now lives — see
// EXT_KINDS below.
//
// A path is MATERIALIZED (`"Geometry/Coupons"`, `''` is the root), mirroring the
// backend's `app/services/repo_paths.py`. Change one side, change both: the
// backend refuses what it will not store, and a client that builds a path the
// backend rejects turns a drag-and-drop into a 400 the user cannot act on.

// ── Extension vocabulary ──────────────────────────────────────────────────
// ONE table behind both the per-file glyph and the kind filter. `fileIcon` used
// to carry its own inline chain of `includes` checks, so adding a filter meant a
// second list of the same extensions — and the two would then disagree about
// what a `.stl` is, showing a file under 🧊 while the Geometry filter hid it.
//
// Order matters only in that the FIRST kind claiming an extension wins; the
// extensions are disjoint today.
export const EXT_KINDS = [
  { kind: 'geometry', label: 'Geometry', icon: '📐', exts: ['stp', 'step', 'igs', 'iges'] },
  { kind: 'mesh', label: 'Mesh', icon: '🧊', exts: ['stl', 'obj', 'glb', 'vtu'] },
  { kind: 'toolpath', label: 'Toolpath', icon: '🧵', exts: ['gcode', 'nc', 'toolpath'] },
  { kind: 'data', label: 'Data', icon: '📋', exts: ['npz', 'json', 'csv', 'tdb'] },
  { kind: 'docs', label: 'Documents', icon: '📄', exts: ['pdf', 'txt', 'md'] },
  { kind: 'image', label: 'Images', icon: '🖼', exts: ['png', 'jpg', 'jpeg', 'gif', 'svg'] },
];

const KIND_BY_EXT = new Map();
for (const entry of EXT_KINDS) {
  for (const ext of entry.exts) KIND_BY_EXT.set(ext, entry);
}

export const OTHER_KIND = { kind: 'other', label: 'Other', icon: '📁', exts: [] };

/** The lowercased extension, or `''` for a name that has none. */
export function extensionOf(name) {
  if (!name || typeof name !== 'string') return '';
  // A leading dot is a hidden file, not an extension — `.gitignore` has none.
  const base = name.slice(name.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

/** The kind slug (`geometry` | `mesh` | … | `other`) for a filename. */
export function kindOf(name) {
  return (KIND_BY_EXT.get(extensionOf(name)) || OTHER_KIND).kind;
}

/** The per-extension glyph. Backs `FileRepository`'s exported `fileIcon`. */
export function iconOf(name) {
  return (KIND_BY_EXT.get(extensionOf(name)) || OTHER_KIND).icon;
}

/** Human label for a kind slug. */
export function kindLabel(kind) {
  return (EXT_KINDS.find((k) => k.kind === kind) || OTHER_KIND).label;
}

/**
 * The kind-filter chips, in EXT_KINDS order with `All` first.
 *
 * Only kinds actually present are offered — a repository of nothing but STEP
 * files should not show five chips that all empty the list. `other` is appended
 * last when something falls through, so an unrecognised extension stays
 * reachable rather than being filterable only by clearing the filter.
 */
export function kindChipsFor(files) {
  const present = new Set((files || []).map((f) => kindOf(f?.fileName)));
  const chips = [{ value: 'all', label: 'All' }];
  for (const entry of EXT_KINDS) {
    if (present.has(entry.kind)) chips.push({ value: entry.kind, label: entry.label });
  }
  if (present.has('other')) chips.push({ value: 'other', label: OTHER_KIND.label });
  return chips;
}

// ── Paths ─────────────────────────────────────────────────────────────────
export const ROOT = '';

/** Normalize loosely — the BACKEND is the authority; this only tidies input. */
export function normalizePath(path) {
  if (!path || typeof path !== 'string') return ROOT;
  return path
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean)
    .join('/');
}

export function parentOf(path) {
  if (!path || !path.includes('/')) return ROOT;
  return path.slice(0, path.lastIndexOf('/'));
}

export function leafOf(path) {
  if (!path) return ROOT;
  return path.slice(path.lastIndexOf('/') + 1);
}

export function childPath(parent, name) {
  const leaf = normalizePath(name);
  if (!leaf) return normalizePath(parent);
  return parent ? `${parent}/${leaf}` : leaf;
}

/**
 * Segment-aware containment.
 *
 * A plain `startsWith` reports `Geometry2` as living inside `Geometry`, which
 * would make the folder tree show a sibling's files under the wrong node.
 */
export function isWithin(path, ancestor) {
  if (!ancestor) return true;
  return path === ancestor || (path || '').startsWith(`${ancestor}/`);
}

/** Breadcrumb entries for a path, root first. */
export function crumbsOf(path) {
  const crumbs = [{ path: ROOT, label: 'All files' }];
  const segments = normalizePath(path).split('/').filter(Boolean);
  let acc = '';
  for (const seg of segments) {
    acc = acc ? `${acc}/${seg}` : seg;
    crumbs.push({ path: acc, label: seg });
  }
  return crumbs;
}

// ── Folder tree ───────────────────────────────────────────────────────────
/**
 * Nest folder rows into a tree, synthesizing any missing intermediate.
 *
 * The synthesis is not defensive padding: a folder can legitimately exist at
 * `A/B` with no row for `A` — the backend's delete reparents a subtree, so
 * removing `A` while `A/B` was being created on another device leaves exactly
 * that. Without synthesis `A/B` would be invisible while still holding files.
 *
 * `fileCounts` is a `{path: n}` map so a node can report its own count; a node
 * with no row of its own reports `folderId: null`, and the caller renders it as
 * a real folder that simply cannot be renamed or deleted.
 */
export function buildFolderTree(folders, fileCounts = {}) {
  const byPath = new Map();

  const ensure = (path) => {
    if (byPath.has(path)) return byPath.get(path);
    const node = {
      path,
      name: leafOf(path),
      folderId: null,
      children: [],
      fileCount: fileCounts[path] || 0,
    };
    byPath.set(path, node);
    const parent = parentOf(path);
    if (path.includes('/')) ensure(parent).children.push(node);
    return node;
  };

  for (const folder of folders || []) {
    const path = normalizePath(folder?.path);
    if (!path) continue;
    ensure(path).folderId = folder.folderId ?? null;
  }

  const roots = [];
  for (const node of byPath.values()) {
    if (!node.path.includes('/')) roots.push(node);
  }

  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const sortDeep = (nodes) => {
    nodes.sort(byName);
    for (const n of nodes) sortDeep(n.children);
  };
  sortDeep(roots);
  return roots;
}

/**
 * The DIRECT subfolders of `folderPath`, as rows for the listing.
 *
 * "All files" is the parent of the top-level folders, so at the root this is
 * every folder with no `/` in its path. Showing them in the list — rather than
 * only in the rail — is what makes the panel navigable the way a file explorer
 * is: the thing you are looking at contains both folders and files, and you
 * open a folder by opening it, not by finding it in a second tree.
 *
 * Counts are SUBTREE totals, not own-folder counts: a folder row saying `0`
 * while its children hold twenty files reads as empty when it is not.
 */
export function subfoldersOf(folders, folderPath = ROOT, counts = {}) {
  // Synthesize missing intermediates, exactly as `buildFolderTree` does — a
  // folder can exist at `A/B` with no row for `A` (the backend's reparenting
  // delete produces that), and if the list and the rail disagreed about whether
  // `A` exists, `A/B` would be reachable from one and invisible from the other.
  const idByPath = new Map();
  for (const folder of folders || []) {
    let path = normalizePath(folder?.path);
    if (!path) continue;
    idByPath.set(path, folder.folderId ?? null);
    while (path.includes('/')) {
      path = parentOf(path);
      if (!idByPath.has(path)) idByPath.set(path, null);
    }
  }

  const here = normalizePath(folderPath);
  const rows = [];
  for (const [path, folderId] of idByPath) {
    if (parentOf(path) !== here || path === here) continue;
    rows.push({ path, name: leafOf(path), folderId, fileCount: subtreeCount(counts, path) });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return rows;
}

/** Files in `path` and everything under it, from a `fileCountsByFolder` map. */
export function subtreeCount(counts, path) {
  let total = 0;
  for (const [key, n] of Object.entries(counts || {})) {
    if (isWithin(key, path)) total += n;
  }
  return total;
}

/** `{path: count}` over the files, counting each file in its own folder only. */
export function fileCountsByFolder(files) {
  const counts = {};
  for (const f of files || []) {
    const p = f?.folderPath || ROOT;
    counts[p] = (counts[p] || 0) + 1;
  }
  return counts;
}

/** Every distinct tag across the files, sorted, deduped case-insensitively. */
export function allTags(files) {
  const seen = new Map();
  for (const f of files || []) {
    for (const tag of f?.tags || []) {
      const key = String(tag).toLowerCase();
      if (!seen.has(key)) seen.set(key, tag);
    }
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

// ── Navigation history ────────────────────────────────────────────────────
// Back/Forward over visited folders, with a browser's semantics: going back and
// then somewhere new DISCARDS the forward entries, because the branch you did
// not take is not somewhere you can return to.
//
// A pure reducer rather than component state so the truncation rule — the part
// that is easy to get subtly wrong and impossible to see — is testable.

export const initialHistory = (path = ROOT) => ({ stack: [path], index: 0 });

export const navPath = (h) => h.stack[h.index];
export const canGoBack = (h) => h.index > 0;
export const canGoForward = (h) => h.index < h.stack.length - 1;

export function navPush(h, path) {
  // Re-selecting where you already are is not a history entry; without this,
  // clicking the current folder in the rail would fill the stack with repeats
  // and Back would appear to do nothing several times over.
  if (navPath(h) === path) return h;
  const stack = [...h.stack.slice(0, h.index + 1), path];
  return { stack, index: stack.length - 1 };
}

export function navBack(h) {
  return canGoBack(h) ? { ...h, index: h.index - 1 } : h;
}

export function navForward(h) {
  return canGoForward(h) ? { ...h, index: h.index + 1 } : h;
}

/**
 * Rewrite a path whose `oldPrefix` part became `newPrefix`.
 *
 * A mirror of the backend's `repo_paths.reparent` — change one, change both.
 * `newPrefix === ROOT` lifts the subtree to the top level, dropping the renamed
 * segment rather than leaving an empty one.
 */
export function reparentPath(path, oldPrefix, newPrefix) {
  if (!isWithin(path, oldPrefix)) return path;
  const remainder = oldPrefix ? path.slice(oldPrefix.length).replace(/^\/+/, '') : path;
  if (!newPrefix) return remainder;
  if (!remainder) return newPrefix;
  return `${newPrefix}/${remainder}`;
}

/**
 * Apply a folder rename or delete across the WHOLE history.
 *
 * Rewriting only the current entry is not enough: Back would then return to a
 * path the rename has just made nonexistent, showing an empty folder that
 * cannot be navigated out of except forwards.
 *
 * A delete collapses several entries onto one place (both `A` and `A/B` become
 * `A`'s parent), so consecutive duplicates are folded and the cursor is carried
 * to wherever its own entry ended up — otherwise Back would step through the
 * same folder twice and read as broken.
 */
export function rewriteHistory(h, oldPath, newPath) {
  const stack = [];
  let index = 0;
  h.stack.forEach((p, i) => {
    const moved = reparentPath(p, oldPath, newPath);
    if (!stack.length || stack[stack.length - 1] !== moved) stack.push(moved);
    if (i === h.index) index = stack.length - 1;
  });
  return { stack, index };
}

// ── Filtering ─────────────────────────────────────────────────────────────
/**
 * The rows to show.
 *
 * `folderPath: null` means "every folder" — what a search across the whole
 * repository needs, and distinct from `''`, which is the root folder ONLY.
 * Conflating them would make a search from the root find nothing filed away.
 *
 * `includeSubfolders` shows a folder's whole subtree rather than just its own
 * files. Off by default, because a folder listing that includes its children's
 * contents cannot show you what is actually IN the folder.
 *
 * Tags are ANDed: picking two narrows, it does not widen. A user adding a second
 * tag to a filter is nearly always trying to be more specific.
 */
export function filterFiles(files, opts = {}) {
  const {
    folderPath = null,
    search = '',
    kinds = 'all',
    tags = [],
    includeSubfolders = false,
  } = opts;

  const q = String(search || '')
    .trim()
    .toLowerCase();
  const kindSet = kinds === 'all' || !kinds ? null : new Set([].concat(kinds));
  const wantTags = (tags || []).map((t) => String(t).toLowerCase());

  return (files || []).filter((f) => {
    if (!f) return false;
    const home = f.folderPath || ROOT;

    if (folderPath !== null) {
      if (includeSubfolders ? !isWithin(home, folderPath) : home !== folderPath) return false;
    }
    if (q && !(f.fileName || '').toLowerCase().includes(q)) return false;
    if (kindSet && !kindSet.has(kindOf(f.fileName))) return false;
    if (wantTags.length) {
      const own = new Set((f.tags || []).map((t) => String(t).toLowerCase()));
      if (!wantTags.every((t) => own.has(t))) return false;
    }
    return true;
  });
}

// ── Sorting ───────────────────────────────────────────────────────────────
export const SORT_FIELDS = ['fileName', 'fileType', 'fileSize', 'createdAt'];

/**
 * Sort a copy of `files`.
 *
 * Lifts the comparator `FileRepository` already had, with two changes.
 * `fileType` sorts by the EXTENSION rather than the stored MIME string, because
 * the Type column renders the extension — sorting a column by a value it does
 * not display is indistinguishable from a broken sort. And the name comparison
 * is numeric-aware, so `part2` precedes `part10`.
 */
export function sortFiles(files, field = 'createdAt', dir = 'desc') {
  const sign = dir === 'asc' ? 1 : -1;
  const key = (f) => {
    if (field === 'fileType') return extensionOf(f?.fileName);
    return f?.[field] ?? '';
  };
  return [...(files || [])].sort((a, b) => {
    const av = key(a);
    const bv = key(b);
    let cmp;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else
      cmp = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    // Ties broken by name so the order never depends on the input order —
    // otherwise re-sorting by Type shuffles same-type rows on every click.
    if (cmp === 0 && field !== 'fileName') {
      return String(a?.fileName || '').localeCompare(String(b?.fileName || ''));
    }
    return cmp * sign;
  });
}

/** `"field-dir"` → `[field, dir]`, defaulting anything unrecognised. */
export function parseSort(sort) {
  const [field, dir] = String(sort || '').split('-');
  return [SORT_FIELDS.includes(field) ? field : 'createdAt', dir === 'asc' ? 'asc' : 'desc'];
}

/**
 * The sort a column header click produces.
 *
 * Clicking the ACTIVE column flips direction; clicking a new one starts at that
 * column's natural direction — descending for size and date (largest/newest
 * first is what someone clicking them wants) and ascending for name and type.
 */
export function nextSort(current, field) {
  const [activeField, activeDir] = parseSort(current);
  if (activeField === field) return `${field}-${activeDir === 'asc' ? 'desc' : 'asc'}`;
  const naturallyDesc = field === 'fileSize' || field === 'createdAt';
  return `${field}-${naturallyDesc ? 'desc' : 'asc'}`;
}

// ── Grouping ──────────────────────────────────────────────────────────────
export const GROUP_OPTIONS = [
  { value: 'none', label: 'No grouping' },
  { value: 'extension', label: 'Group by extension' },
  { value: 'kind', label: 'Group by type' },
];

/**
 * Partition into display groups, preserving the order `files` arrives in.
 *
 * `none` returns ONE unlabelled group rather than a bare array, so the table
 * renders one code path instead of branching on whether grouping is on.
 * Group order follows first appearance, which means it follows the active sort
 * — grouping by extension while sorted by size puts the largest file's
 * extension first, which is what makes the two controls compose.
 *
 * With grouping ON and nothing to group this returns NO groups, where `none`
 * returns one empty one. That asymmetry is deliberate: a group is named after
 * its members, so there is no honest heading to render for zero files. Callers
 * decide between a list and the empty state from the FILE count, never from the
 * group count.
 */
export function groupFiles(files, groupBy = 'none') {
  const list = files || [];
  if (groupBy !== 'extension' && groupBy !== 'kind') {
    return [{ key: 'all', label: null, files: list }];
  }
  const groups = new Map();
  for (const f of list) {
    const key =
      groupBy === 'extension' ? extensionOf(f?.fileName) || 'no extension' : kindOf(f?.fileName);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label:
          groupBy === 'extension'
            ? `.${key}`.replace('.no extension', 'No extension')
            : kindLabel(key),
        files: [],
      });
    }
    groups.get(key).files.push(f);
  }
  return [...groups.values()];
}
